import { Component, Host, Element, Prop, State, Watch, Event, EventEmitter, h } from '@stencil/core';
import { arrow_left, arrow_right } from 'pn-design-assets/pn-assets/icons.js';

import { en, awaitTopbar } from '@/index';
import type { PnLanguages, PnPaginationItem } from '@/index';

import { translations } from './translations';

/**
 * Accessible pagination needs each item to link to a different part of the document.
 * This means that you must `preventDefault` on the `MouseEvent` emitted from the `pageSelected` event if you have a SPA.
 *
 * If you set a `page` value that is higher than `pages`, the component will automatically lower it to the `pages` value.
 */
@Component({
  tag: 'pn-pagination',
  styleUrl: 'pn-pagination.scss',
})
export class PnPagination {
  listElement: HTMLUListElement;

  @Element() hostElement: HTMLElement;

  @State() list: PnPaginationItem[];

  /** Set a label for the pagination element for screen readers. It defaults to `Pagination`. */
  @Prop() label?: string;
  /** Set a custom URL template. Use `{page}` within the string to position where the dynamic page number should be. */
  @Prop() urlTemplate: string = '?page={page}';
  /** Set a HTML id on the pagination element. */
  @Prop() paginationId?: string = null;
  /** Manually set the language. */
  @Prop() language?: PnLanguages = null;

  /**
   * Set which page the user is currently viewing.
   * Use this to set an inital page or if the selected page is changed from outside this component.
   *
   * @category Pages
   **/
  @Prop({ mutable: true, reflect: true }) page: number = 1;
  /** Set how many pages are available. @category Pages */
  @Prop({ mutable: true }) pages!: number;
  /** Set how many pages should be visible at one time. Use an odd number. Minimum is `5`. @category Pages */
  @Prop({ mutable: true }) pagesVisible: number = 5;

  /**
   * Allow the user to decide how many items should be shown per page.
   * Make sure this value exists in the `rowsList` string.
   *
   * @see {@link rowsList}
   * @category Rows per page
   * */
  @Prop({ mutable: true }) rows: number = null;
  /**
   * Set available items per page options the user can select. Use a comma separated string.
   *
   * @see {@link rows}
   * @category Rows per page
   * */
  @Prop() rowsList: string = '10,25,50';
  /**
   * Default label is "Items per page".
   * @see {@link rows}
   * @category Rows per page
   * */
  @Prop() rowsLabel: string = null;

  @Watch('page')
  handlePage() {
    if (this.page > this.pages) this.page = this.pages;
    else if (this.page < 1) this.page = 1;
  }

  @Watch('pages')
  handlePages() {
    const list = [];

    if (this.pages > 10000) {
      console.error(`${this.hostElement.localName}: Invalid page count ${this.pages}. Maximum is 10000.`);
      this.pages = 10000;
    }

    for (let index = 0; this.pages > index; index++) {
      const page = index + 1;
      list.push({ index, page });
    }

    this.list = list;
    this.handlePage();
  }

  @Watch('pagesVisible')
  handlePagesVisible() {
    if (5 > this.pagesVisible) this.pagesVisible = 5;
    if (this.pagesVisible % 2 === 0) this.pagesVisible = Math.round(this.pagesVisible) - 1;
  }

  /** Emitted when the page is changed. Either via clicking a link or selecting a page in the select. */
  @Event() pageSelected: EventEmitter<{ page: number; mouse: MouseEvent }>;
  /** This event fires when the user changes how many items should be shown per page. */
  @Event() rowsSelected: EventEmitter<{ rows: number; change: Event }>;

  async componentWillLoad() {
    this.handlePagesVisible();
    this.handlePages();
    if (this.language === null) await awaitTopbar(this.hostElement);
  }

  private translate(prop: keyof typeof translations) {
    return translations[prop][this.language || en];
  }

  private isActivePage(page: number): boolean {
    return page === this.page;
  }

  private navigate({ page, event, focus }: { page?: number; event: MouseEvent; focus?: boolean }) {
    if (typeof page === 'number' && page >= 1 && this.pages >= page) this.page = page;

    this.pageSelected.emit({ page: this.page, mouse: event });

    if (focus)
      requestAnimationFrame(() => {
        const element = this.listElement.querySelector<HTMLLinkElement>('a[aria-current="page"]');
        element?.focus({ preventScroll: true });
      });
  }

  private useRowList() {
    return !!this.rows && !!this.rowsList;
  }

  private setRows(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.rows = Number(target.value);
    this.rowsSelected.emit({ rows: this.rows, change: event });
  }

  private getRowList() {
    return this.rowsList.split(',').map(item => Number(item.trim()));
  }

  /** Get the page numbers of the lowest and highest visible page. */
  private getPaginationIndex() {
    const current = this.page;

    let low = current - Math.ceil(this.pagesVisible / 2);
    let high = current + Math.floor(this.pagesVisible / 2);

    for (let i = 0; this.pagesVisible > i; i++) {
      if (low < 0) {
        high++;
        low++;
      } else if (high > this.pages) {
        high--;
        low--;
      }
    }

    const lowest = 0 > low ? 0 : low;

    const leftIndent = lowest >= 1;
    const rightIndent = this.pages > high;

    return {
      low: rightIndent && leftIndent ? lowest + 1 : lowest,
      high: rightIndent && leftIndent ? high - 1 : high,
      leftIndent,
      rightIndent,
    };
  }

  private getPagination() {
    const { low, high } = this.getPaginationIndex();
    return this.list.slice(low, high);
  }

  private getUrl(page: number): string {
    return this.setPageText(this.urlTemplate, page);
  }

  private setPageText(text: string, page: number) {
    return text.replace('{page}', page.toString());
  }

  private getPrevPage() {
    let page = this.page;
    return page > 1 ? (page -= 1) : 1;
  }

  private getNextPage() {
    let page = this.page;
    return this.pages > page ? (page += 1) : this.pages;
  }

  private showIntendation(last = false) {
    const { leftIndent, rightIndent } = this.getPaginationIndex();
    return last ? rightIndent : leftIndent;
  }

  private renderDivider(last = false): HTMLSpanElement {
    return (
      <span class="pn-pagination-dot" data-show={this.showIntendation(last)}>
        ...
      </span>
    );
  }

  private renderButton({ page, dataAttr }: { page: number; dataAttr?: string }) {
    const active = this.isActivePage(page);
    return (
      <pn-button
        label={page.toString()}
        arialabel={`${this.translate('PAGE')} ${page}`}
        ariacurrent={active ? 'page' : null}
        href={this.getUrl(page)}
        variant={active ? 'outlined' : ''}
        appearance="light"
        small
        data-arrow={dataAttr}
        data-show={dataAttr && this.showIntendation(dataAttr === 'last')}
        onPnClick={({ detail }) => this.navigate({ page, event: detail, focus: true })}
      />
    );
  }

  private renderItem({ page }: PnPaginationItem) {
    return (
      <li class="pn-pagination-list-item" aria-setsize={this.pages} aria-posinset={page}>
        {this.renderButton({ page })}
      </li>
    );
  }

  private renderJumpButton(last = false) {
    const rel = last ? 'next' : 'prev';
    const icon = last ? arrow_right : arrow_left;

    const page = last ? this.getNextPage() : this.getPrevPage();

    const current = last ? this.pages === this.page : 1 === this.page;

    const text = last ? 'NEXT_PAGE' : 'PREVIOUS_PAGE';
    const showPageNumber = current ? '' : ` (${page})`;
    const label = `${this.translate(text)}${showPageNumber}`;

    return (
      <pn-button
        arialabel={label}
        ariacurrent={current ? 'page' : null}
        href={this.getUrl(page)}
        rel={current ? null : rel}
        appearance="light"
        small
        icon={icon}
        iconOnly
        onPnClick={({ detail }) => this.navigate({ page, event: detail })}
      />
    );
  }

  render() {
    return (
      <Host>
        <nav id={this.paginationId} class="pn-pagination" aria-label={this.label || this.translate('PAGINATION')}>
          {this.useRowList() && (
            <pn-select
              class="pn-pagination-rows"
              label={this.rowsLabel || this.translate('ROWS_PAGE')}
              onChange={event => this.setRows(event)}
            >
              {this.getRowList().map(item => (
                <option value={item} selected={this.rows === Number(item)} innerHTML={`${item}`} />
              ))}
            </pn-select>
          )}

          <div class="pn-pagination-container" data-right={this.useRowList()}>
            {this.renderJumpButton()}
            {this.renderButton({ page: 1, dataAttr: 'first' })}
            {this.renderDivider()}

            <ol class="pn-pagination-list" ref={el => (this.listElement = el)}>
              {this.getPagination().map(item => this.renderItem(item))}
            </ol>

            {this.renderDivider(true)}
            {this.renderButton({ page: this.pages, dataAttr: 'last' })}
            {this.renderJumpButton(true)}
          </div>
        </nav>
      </Host>
    );
  }
}
